import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Navigation from "../components/Navigation";
import axios from "axios";
import { 
  BarChart3, Users, TrendingUp, Heart, Target, MessageCircle,
  Calendar, Activity, Award, Sparkles, Mail, Send, Eye, Check, X
} from "lucide-react";
import { toast } from "sonner";

export default function Analytics() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(7);
  const [emailPrefs, setEmailPrefs] = useState({ enabled: true });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchAnalytics();
    fetchEmailPrefs();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analytics/dashboard?days=${period}`);
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailPrefs = async () => {
    try {
      const response = await axios.get(`${API}/analytics/email-preferences`);
      setEmailPrefs(response.data);
    } catch (error) {
      console.error("Failed to fetch email preferences:", error);
    }
  };

  const toggleEmailReports = async () => {
    try {
      const newEnabled = !emailPrefs.enabled;
      await axios.put(`${API}/analytics/email-preferences?enabled=${newEnabled}`);
      setEmailPrefs({ enabled: newEnabled });
      toast.success(`Weekly reports ${newEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error("Failed to update preferences");
    }
  };

  const sendReportNow = async () => {
    try {
      setSendingEmail(true);
      const response = await axios.post(`${API}/analytics/send-report`, {});
      if (response.data.status === 'success') {
        toast.success("Report sent to your email!");
      } else if (response.data.status === 'skipped') {
        toast.info("Email not configured. Add RESEND_API_KEY to enable.");
      } else {
        toast.error(response.data.message || "Failed to send email");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send report");
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <Navigation />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-48 bg-amber-200 rounded"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-white rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, subtext, color = "amber" }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-2 rounded-lg bg-${color}-100`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <div className="text-3xl font-bold text-gray-900">{value}</div>
      {subtext && <div className="text-sm text-gray-500 mt-1">{subtext}</div>}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <Navigation />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-amber-600" />
              Analytics Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Track your community's engagement and growth</p>
          </div>
          
          <div className="mt-4 md:mt-0 flex flex-wrap gap-2">
            {/* Period selector */}
            {[7, 14, 30].map((days) => (
              <button
                key={days}
                onClick={() => setPeriod(days)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  period === days
                    ? "bg-amber-600 text-white"
                    : "bg-white text-gray-600 hover:bg-amber-50"
                }`}
              >
                {days}D
              </button>
            ))}
            
            {/* Email report actions */}
            <button
              onClick={() => setShowPreview(true)}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-white text-gray-600 hover:bg-gray-50 flex items-center gap-2"
            >
              <Eye className="w-4 h-4" /> Preview
            </button>
            <button
              onClick={sendReportNow}
              disabled={sendingEmail}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-2 disabled:opacity-50"
            >
              {sendingEmail ? (
                <>Sending...</>
              ) : (
                <>
                  <Send className="w-4 h-4" /> Send Report
                </>
              )}
            </button>
          </div>
        </div>

        {/* Email Preferences Card */}
        <div className="bg-white rounded-xl p-4 mb-6 shadow-sm border border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100">
              <Mail className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Weekly Email Reports</h3>
              <p className="text-sm text-gray-500">Receive analytics summary every Monday</p>
            </div>
          </div>
          <button
            onClick={toggleEmailReports}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              emailPrefs.enabled ? 'bg-amber-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                emailPrefs.enabled ? 'left-7' : 'left-1'
              }`}
            />
          </button>
        </div>

        {/* Email Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold text-lg">Email Report Preview</h3>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <iframe
                src={`${API}/analytics/report-preview`}
                className="w-full h-[70vh]"
                title="Email Preview"
              />
            </div>
          </div>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Users}
            label="Active Users"
            value={data?.summary?.active_users || 0}
            subtext={`of ${data?.summary?.total_users || 0} total`}
            color="blue"
          />
          <StatCard
            icon={Target}
            label="Goals Completed"
            value={data?.summary?.goals_completed || 0}
            subtext="Total achievements"
            color="green"
          />
          <StatCard
            icon={MessageCircle}
            label="Total Posts"
            value={data?.summary?.total_posts || 0}
            subtext="Community content"
            color="purple"
          />
          <StatCard
            icon={TrendingUp}
            label="Retention Rate"
            value={`${data?.retention?.retention_rate || 0}%`}
            subtext="7-day retention"
            color="amber"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Daily Active Users */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-amber-600" />
              Daily Active Users
            </h3>
            <div className="space-y-3">
              {data?.daily_active_users?.length > 0 ? (
                data.daily_active_users.map((day, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="text-sm text-gray-500 w-24">{day.date}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-amber-400 to-orange-500 h-full rounded-full flex items-center justify-end pr-2"
                        style={{
                          width: `${Math.min(
                            (day.users / Math.max(...data.daily_active_users.map((d) => d.users))) * 100,
                            100
                          )}%`,
                        }}
                      >
                        <span className="text-xs font-medium text-white">{day.users}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">No data for this period</p>
              )}
            </div>
          </div>

          {/* Feature Usage */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-600" />
              Most Used Features
            </h3>
            <div className="space-y-3">
              {data?.summary?.top_features?.length > 0 ? (
                data.summary.top_features.slice(0, 6).map((feature, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs flex items-center justify-center font-medium">
                        {idx + 1}
                      </span>
                      <span className="text-gray-700 capitalize">{feature.feature || "Unknown"}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-gray-900 font-medium">{feature.total_uses}</span>
                      <span className="text-gray-400 text-sm ml-1">uses</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">Start using features to see analytics</p>
              )}
            </div>
          </div>
        </div>

        {/* Popular Content */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-600" />
            Top Performing Content
          </h3>
          
          {data?.popular_content?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.popular_content.map((post, idx) => (
                <div key={idx} className="border border-gray-100 rounded-lg p-4 hover:border-amber-200 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                      #{idx + 1}
                    </span>
                    <span className="text-sm text-gray-500">{post.user_name}</span>
                  </div>
                  <p className="text-gray-700 text-sm line-clamp-2 mb-3">{post.content}</p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1 text-red-500">
                      <Heart className="w-4 h-4" /> {post.hearts || 0}
                    </span>
                    <span className="flex items-center gap-1 text-yellow-500">
                      ⭐ {post.stars || 0}
                    </span>
                    <span className="flex items-center gap-1 text-pink-500">
                      🤗 {post.hugs || 0}
                    </span>
                    <span className="flex items-center gap-1 text-gray-500">
                      <MessageCircle className="w-4 h-4" /> {post.comment_count || 0}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Create posts to see popular content</p>
          )}
        </div>

        {/* Event Breakdown */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-amber-600" />
            Activity Breakdown
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(data?.summary?.events || {}).map(([event, count]) => (
              <div key={event} className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{count}</div>
                <div className="text-sm text-gray-500 capitalize">{event.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Retention Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <div className="text-sm opacity-80">New Users (7 days)</div>
            <div className="text-3xl font-bold mt-1">{data?.retention?.new_users_7d || 0}</div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
            <div className="text-sm opacity-80">New Users (30 days)</div>
            <div className="text-3xl font-bold mt-1">{data?.retention?.new_users_30d || 0}</div>
          </div>
          <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-6 text-white">
            <div className="text-sm opacity-80">Total Community Size</div>
            <div className="text-3xl font-bold mt-1">{data?.retention?.total_users || 0}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
